import { useCallback, useEffect, useState } from 'react';
import { type AuditEvent, fetchAuditLogs } from '@/api/superadmin';
import { Button } from '@/components/primitives/button';
import { Skeleton } from '@/components/primitives/skeleton';
import { SuperAdminPage } from '@/components/superadmin/superadmin-layout';

const PAGE_SIZE = 50;

/** Only the actions this console can produce; the enum has more, used elsewhere. */
const ACTIONS = [
  'superadmin_login_succeeded',
  'superadmin_login_failed',
  'tenant_created',
  'tenant_invitation_sent',
  'tenant_secrets_viewed',
  'tenant_admin_email_changed',
  'tenant_admin_password_reset_sent',
  'email_change_requested',
  'email_change_confirmed',
  'password_changed',
  'webhook_signature_rejected',
];

export function SuperAdminAuditPage() {
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      const result = await fetchAuditLogs({ action: action || undefined, page, limit: PAGE_SIZE });
      setEvents(result.events);
      setTotal(result.total);
    } catch (caught) {
      setError((caught as Error).message);
      setEvents([]);
    }
  }, [action, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <SuperAdminPage
      title="Audit log"
      description="Append-only. Recorded actions cannot be edited or removed from here."
    >
      <select
        value={action}
        onChange={(event) => {
          setPage(0);
          setAction(event.target.value);
        }}
        className="border-stroke-soft bg-bg-white text-paragraph-sm text-text-strong max-w-xs rounded-lg border px-3 py-2"
      >
        <option value="">All actions</option>
        {ACTIONS.map((value) => (
          <option key={value} value={value}>
            {value.replace(/_/g, ' ')}
          </option>
        ))}
      </select>

      {error && <p className="text-paragraph-sm text-error-base">{error}</p>}

      <div className="border-stroke-soft bg-bg-white overflow-hidden rounded-xl border">
        <table className="w-full text-left">
          <thead className="border-stroke-soft bg-bg-weak border-b">
            <tr>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">When</th>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Action</th>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Actor</th>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Target</th>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Result</th>
            </tr>
          </thead>
          <tbody>
            {events === null && (
              <tr>
                <td colSpan={5} className="px-5 py-6">
                  <Skeleton className="h-5 w-full" />
                </td>
              </tr>
            )}

            {events?.length === 0 && (
              <tr>
                <td colSpan={5} className="text-paragraph-sm text-text-soft px-5 py-8 text-center">
                  Nothing recorded for this filter.
                </td>
              </tr>
            )}

            {events?.map((event) => (
              <tr key={event._id} className="border-stroke-soft border-b last:border-b-0">
                <td className="text-paragraph-sm text-text-sub px-5 py-3 whitespace-nowrap">
                  {new Date(event.createdAt).toLocaleString()}
                </td>
                <td className="text-paragraph-sm text-text-strong px-5 py-3">{event.action.replace(/_/g, ' ')}</td>
                <td className="text-paragraph-sm text-text-sub px-5 py-3">
                  {event.actorId ?? '—'}
                  {event.actorIp && <span className="text-text-soft"> · {event.actorIp}</span>}
                </td>
                <td className="text-paragraph-xs text-text-soft px-5 py-3">
                  {event.targetType ? `${event.targetType} ${event.targetId ?? ''}`.trim() : '—'}
                </td>
                <td className="px-5 py-3">
                  {event.success ? (
                    <span className="text-paragraph-xs text-success-base">OK</span>
                  ) : (
                    <span className="text-paragraph-xs text-error-base" title={event.failureReason}>
                      Refused
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-paragraph-xs text-text-soft">
            Page {page + 1} of {lastPage + 1} · {total} events
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              mode="outline"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              mode="outline"
              disabled={page >= lastPage}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </SuperAdminPage>
  );
}

export default SuperAdminAuditPage;
